import { GlobalStyles } from '@mui/material';
import {
  useAccessibilityPrefsStore,
  FONT_FAMILY_OPTIONS,
} from '@/stores/useAccessibilityPrefsStore';

/**
 * Global CSS overrides for accessibility:
 *
 * - Focus-visible ring
 * - Reduced-motion (OS preference OR user toggle)
 * - User-configurable font size, font family, line-height, letter-spacing
 * - Underline links, highlight focus, sr-only utility
 */
export const AccessibilityStyles: React.FC = () => {
  const {
    fontScale,
    fontFamily,
    lineHeightLoose,
    wideLetterSpacing,
    underlineLinks,
    highlightFocus,
    reduceMotion,
  } = useAccessibilityPrefsStore();

  const fontFamilyCss =
    FONT_FAMILY_OPTIONS.find((o) => o.value === fontFamily)?.css ??
    FONT_FAMILY_OPTIONS[0]?.css ?? '"Roboto", "Helvetica", "Arial", sans-serif';

  return (
    <GlobalStyles
      key={`a11y-${fontScale}-${fontFamily}-${lineHeightLoose}-${wideLetterSpacing}-${underlineLinks}-${highlightFocus}-${reduceMotion}`}
      styles={(theme) => ({
        /* ---- User font preferences ---- */
        'html': {
          fontSize: `${fontScale * 100}%`,
          ...(fontFamily !== 'default' && { fontFamily: fontFamilyCss }),
          ...(wideLetterSpacing && { letterSpacing: '0.05em' }),
        },
        ...(fontFamily !== 'default' && {
          'body, button, input, select, textarea, .MuiTypography-root, .MuiButton-root, .MuiInputBase-root': {
            fontFamily: `${fontFamilyCss} !important`,
          },
        }),
        ...(lineHeightLoose && {
          'p, li, td, th, h1, h2, h3, h4, h5, h6, .MuiTypography-root, .MuiMenuItem-root, .MuiListItemText-root, .MuiTableCell-root, .MuiFormControlLabel-label, .MuiChip-label, .MuiCardContent-root, .MuiDialogContent-root, .MuiAlert-message': {
            lineHeight: '1.8 !important',
          },
        }),

        /* ---- Focus ring ---- */
        ...(highlightFocus
          ? {
              // Keyboard focus
              '*:focus-visible': {
                outline: `2px solid ${theme.palette.secondary.main} !important`,
                outlineOffset: '2px !important' as string,
              },
              // MUI's own focus classes (triggered on both mouse and keyboard)
              '.Mui-focusVisible': {
                outline: `2px solid ${theme.palette.secondary.main} !important`,
                outlineOffset: '2px !important' as string,
              },
              // Buttons, icons, menu items — only on keyboard focus
              '.MuiButtonBase-root:focus-visible, .MuiIconButton-root:focus-visible, .MuiMenuItem-root:focus-visible, .MuiListItemButton-root:focus-visible, .MuiTab-root:focus-visible, .MuiSelect-select:focus-visible': {
                outline: `2px solid ${theme.palette.secondary.main} !important`,
                outlineOffset: '2px !important' as string,
              },
              // Input fields when focused — use border highlight instead of outline
              '.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: `${theme.palette.secondary.main} !important`,
                borderWidth: '2px !important',
              },
              // Suppress outline on inputs/textareas — the border change is enough
              '.MuiInputBase-root:focus-within, .MuiInputBase-root.Mui-focused, .MuiInputBase-root input:focus-visible, .MuiInputBase-root textarea:focus-visible, .MuiInputBase-root input:focus, .MuiInputBase-root textarea:focus, .MuiSelect-select:focus': {
                outline: 'none !important',
              },
            }
          : {
              '*:focus-visible': {
                outline: `2px solid ${theme.palette.secondary.main}`,
                outlineOffset: 2,
              },
              '.MuiButtonBase-root:focus-visible': {
                outline: `2px solid ${theme.palette.secondary.main}`,
                outlineOffset: 2,
              },
            }),

        /* ---- Underline links ---- */
        ...(underlineLinks && {
          'a, a:visited, .MuiLink-root, .MuiListItemButton-root, .MuiTab-root, .MuiBreadcrumbs-li a, [role="link"]': {
            textDecoration: 'underline !important',
            textDecorationThickness: '2px !important',
            textUnderlineOffset: '3px !important' as string,
          },
          '.MuiListItemButton-root .MuiListItemText-primary': {
            textDecoration: 'underline !important',
            textDecorationThickness: '2px !important',
            textUnderlineOffset: '3px !important' as string,
          },
          '.MuiButton-text, .MuiButton-outlined': {
            textDecoration: 'underline !important',
            textDecorationThickness: '2px !important',
            textUnderlineOffset: '3px !important' as string,
          },
        }),

        /* ---- Reduced motion (OS or user toggle) ---- */
        ...((reduceMotion ? {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important',
          },
        } : {
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        })),

        /* ---- Visually-hidden utility ---- */
        '.sr-only': {
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        },
      })}
    />
  );
};
